import { useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, Store } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { useMyPassbooks } from '@/shared/lib/my-passbooks';
import { passbookIdsOf } from '@/shared/lib/passbook-access';
import { setLastPassbookId } from '@/shared/lib/last-passbook';
import { cn } from '@/shared/lib/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';

/**
 * Troca de marca na sidebar.
 *
 * Só aparece para conta de grupo, com mais de uma loja vinculada. Com uma loja
 * só, que é o caso de quase todo mundo, não renderiza nada e a tela fica igual.
 *
 * Existe para a marca atual estar sempre à vista: sem isso, quem atende duas
 * marcas no mesmo login cadastra campanha na marca errada sem perceber.
 */
export function BrandSwitcher({
  collapsed,
  currentId,
  onNavigate,
}: {
  collapsed?: boolean;
  currentId?: string;
  onNavigate?: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const ids = passbookIdsOf(user);
  const { passbooks } = useMyPassbooks({ enabled: ids.length > 1 });

  if (ids.length < 2) return null;

  const named = passbooks.filter((p) => ids.includes(p._id));
  const options = named.length > 0 ? named : ids.map((id) => ({ _id: id, organizationName: undefined }));
  const current = options.find((p) => p._id === currentId);
  const label = current?.organizationName ?? (current ? 'Loja atual' : 'Escolher marca');

  const go = (id: string) => {
    if (id === currentId) return;
    setLastPassbookId(id);
    onNavigate?.();
    navigate(`/loja/${id}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={collapsed ? label : undefined}
          aria-label={`Marca atual: ${label}. Trocar de marca`}
          className={cn(
            'flex w-full items-center rounded-xl border border-border/60 p-2 text-left transition-colors hover:bg-accent/60',
            collapsed ? 'justify-center' : 'gap-3',
          )}
        >
          <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-primary/12 text-primary">
            <Store className="size-4" />
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                  Marca
                </span>
                <span className="block truncate text-sm font-medium">{label}</span>
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel>Trocar de marca</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((p) => (
          <DropdownMenuItem key={p._id} onClick={() => go(p._id)} className="gap-2">
            <Check className={cn('size-4 shrink-0', p._id === currentId ? 'opacity-100' : 'opacity-0')} />
            <span className="min-w-0 truncate">{p.organizationName ?? p._id}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
